import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  disablePush,
  ensurePushForCurrentUser,
  hasActiveSubscription,
  pushSupported,
  readPushPermission,
  requestAndSubscribe,
} from '@/platform/push';

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

export interface PushNotificationsContextValue {
  permission: PushPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  /** Activa push de forma optimista. Devuelve false si falló/denegó (ya revertido). */
  subscribe: () => Promise<boolean>;
  /** Desactiva push de forma optimista. Devuelve false si falló (ya revertido). */
  unsubscribe: () => Promise<boolean>;
}

const PushNotificationsContext = createContext<PushNotificationsContextValue | undefined>(undefined);

function readPermission(): PushPermission {
  return readPushPermission();
}

/**
 * Fuente única de verdad del estado de push para toda la app: Ajustes y el
 * drawer comparten esta instancia, así que nunca pueden divergir. Revalida
 * contra la realidad del navegador (`getSubscription()`) al montar y cada vez
 * que la pestaña recupera foco/visibilidad, de modo que si el navegador
 * invalida la suscripción mientras la app está abierta, la UI lo refleja.
 */
export function PushNotificationsProvider({ children }: { children: ReactNode }) {
  const [permission, setPermission] = useState<PushPermission>(readPermission);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!pushSupported) {
      setPermission('unsupported');
      setIsSubscribed(false);
      return;
    }
    setPermission(readPushPermission());
    setIsSubscribed(await hasActiveSubscription());
  }, []);

  // Montaje: re-vincula la suscripción existente al usuario actual y refleja
  // el estado real.
  useEffect(() => {
    let aborted = false;
    void (async () => {
      await ensurePushForCurrentUser();
      if (!aborted) await refresh();
    })();
    return () => {
      aborted = true;
    };
  }, [refresh]);

  // Revalida al volver el foco / visibilidad a la pestaña.
  useEffect(() => {
    if (!pushSupported) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!pushSupported) return false;
    setIsLoading(true);
    setIsSubscribed(true); // optimista: el toggle cambia al instante
    try {
      const ok = await requestAndSubscribe();
      setPermission(readPushPermission());
      setIsSubscribed(ok); // confirma o revierte (permiso denegado / fallo)
      return ok;
    } catch (err) {
      console.error('Push subscribe error:', err);
      setIsSubscribed(false); // revertir
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!pushSupported) return false;
    setIsLoading(true);
    setIsSubscribed(false); // optimista
    try {
      await disablePush();
      return true;
    } catch (err) {
      console.error('Push unsubscribe error:', err);
      setIsSubscribed(true); // revertir
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo<PushNotificationsContextValue>(
    () => ({ permission, isSubscribed, isLoading, subscribe, unsubscribe }),
    [permission, isSubscribed, isLoading, subscribe, unsubscribe],
  );

  return <PushNotificationsContext.Provider value={value}>{children}</PushNotificationsContext.Provider>;
}

export function usePushNotificationsContext(): PushNotificationsContextValue {
  const ctx = useContext(PushNotificationsContext);
  if (!ctx) {
    throw new Error('usePushNotifications must be used within a PushNotificationsProvider');
  }
  return ctx;
}
